import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ReportJobI } from 'Api/axios/report-jobs-api';

/** Estados en los que el reporte todavía se está generando. */
export const ACTIVE_JOB_STATUSES = ['pending', 'processing'] as const;

export const isJobActive = (job: ReportJobI): boolean =>
  (ACTIVE_JOB_STATUSES as readonly string[]).includes(job.status);

interface ReportJobsState {
  /** Lo último que respondió el servidor. */
  jobs: ReportJobI[];
  /** Panel plegado a solo el encabezado. */
  collapsed: boolean;
  /**
   * Reportes que el usuario cerró con la ✕. Siguen existiendo en el servidor;
   * simplemente ya no los quiere ver.
   *
   * Se guardan en el navegador y no en el backend porque es una preferencia
   * del usuario, no del reporte. Pero **sí sobreviven a una recarga**: el
   * listado del servidor devuelve los últimos veinte sin saber cuáles se
   * cerraron, así que en memoria pura reaparecían todos al refrescar.
   */
  dismissedIds: string[];
  /**
   * Reportes cuyo desenlace ya se atendió: se disparó su descarga, o se avisó
   * de su error. Evita repetir la descarga y el aviso en cada consulta.
   */
  handledIds: string[];
}

interface ReportJobsActions {
  setJobs(jobs: ReportJobI[]): void;
  upsertJob(job: ReportJobI): void;
  markHandled(jobId: string): void;
  dismiss(jobId: string): void;
  dismissAll(): void;
  toggleCollapsed(): void;
}

export const useReportJobsStore = create<ReportJobsState & ReportJobsActions>()(
  persist(
    (set, get) => ({
      jobs: [],
      collapsed: false,
      dismissedIds: [],
      handledIds: [],

      setJobs(jobs) {
        // Los cerrados se podan contra lo que acaba de llegar. El servidor solo
        // devuelve los últimos veinte, así que el id de uno que ya salió de esa
        // ventana no volverá a aparecer: guardarlo sería basura sin tope.
        const alive = new Set(jobs.map(job => job.id));

        set(state => ({
          jobs,
          dismissedIds: state.dismissedIds.filter(id => alive.has(id)),
        }));
      },

      /**
       * Mete un reporte recién solicitado sin esperar a la siguiente consulta, para
       * que la ventana aparezca en el mismo instante en que se pide.
       */
      upsertJob(job) {
        set(state => ({
          jobs: [job, ...state.jobs.filter(current => current.id !== job.id)],
          // Si vuelve a pedirse uno que se había cerrado, que reaparezca.
          dismissedIds: state.dismissedIds.filter(id => id !== job.id),
        }));
      },

      markHandled(jobId) {
        if (get().handledIds.includes(jobId)) return;
        set(state => ({ handledIds: [...state.handledIds, jobId] }));
      },

      dismiss(jobId) {
        set(state => ({ dismissedIds: [...state.dismissedIds, jobId] }));
      },

      dismissAll() {
        set(state => {
          const alreadyDismissed = state.dismissedIds;
          const newlyDismissed = state.jobs.map(job => job.id).filter(id => !alreadyDismissed.includes(id));

          return {
            dismissedIds: [...alreadyDismissed, ...newlyDismissed],
            collapsed: false,
          };
        });
      },

      toggleCollapsed() {
        set(state => ({ collapsed: !state.collapsed }));
      },
    }),
    {
      name: 'aikon.reportJobs',
      storage: createJSONStorage(() => localStorage),
      // Solo la preferencia. `jobs` es lo que respondió el servidor y se vuelve
      // a pedir al montar; guardarlo pintaría por un instante datos viejos.
      partialize: state => ({ dismissedIds: state.dismissedIds }),
    }
  )
);

/** Los reportes que la ventana debe mostrar ahora mismo. */
export const selectVisibleJobs = (state: ReportJobsState): ReportJobI[] =>
  state.jobs.filter(job => !state.dismissedIds.includes(job.id));
